import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import { ScrollArea, ScrollBar } from "@/components/ui/ScrollArea";
import { ChevronDownIcon, Check, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

export interface GroupSearchDropdownItem {
  id?: string;
  name: string;
}

export interface GroupSearchDropdownGroup<T extends GroupSearchDropdownItem> {
  groupName: string;
  items: T[];
}

type SingleProps = {
  multiple?: false;
  value: unknown;
  onChange: (value: string | undefined) => void;
}

type MultipleProps = {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

type BaseProps<TItem extends GroupSearchDropdownItem> = {
  groups: GroupSearchDropdownGroup<TItem>[];
  itemToValue?: (item: TItem) => string;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  errorMessage?: string;
  className?: string;
  inputClassname?: string;
  placeholderClassname?: string;
}

export type GroupSearchDropdownProps<TItem extends GroupSearchDropdownItem> =
  BaseProps<TItem> & (SingleProps | MultipleProps);

export function GroupSearchDropdown<TItem extends GroupSearchDropdownItem>({
  groups,
  itemToValue,
  label,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  noResultsText = "No results found.",
  errorMessage,
  className,
  inputClassname,
  placeholderClassname,
  ...modeProps
}: GroupSearchDropdownProps<TItem>) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const multiple = modeProps.multiple === true;

  const resolveValue = React.useCallback(
    (item: TItem) => (itemToValue ? itemToValue(item) : item.id),
    [itemToValue]
  );

  const allItems = React.useMemo(
    () => groups.flatMap((g) => g.items),
    [groups]
  );

  const filteredGroups = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return groups;

    return groups
      .map((group) => {
        const groupMatches = group.groupName.toLowerCase().includes(query);
        if (groupMatches) return group;
        const matchingItems = group.items.filter((item) =>
          item.name.toLowerCase().includes(query)
        );
        return { ...group, items: matchingItems };
      })
      .filter((group) => group.items.length > 0);
  }, [groups, search]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const singleValue = !multiple ? (modeProps as SingleProps).value : undefined;
  const singleOnChange = !multiple
    ? (modeProps as SingleProps).onChange
    : undefined;

  const handleSingleSelect = (item: TItem) => {
    singleOnChange?.(resolveValue(item));
    setOpen(false);
    setSearch("");
  };

  const selectedSingle = allItems.find((item) => resolveValue(item) == singleValue);

  const multiValue: string[] = multiple
    ? ((modeProps as MultipleProps).value ?? [])
    : [];
  const multiOnChange = multiple
    ? (modeProps as MultipleProps).onChange
    : undefined;

  const isItemSelected = (item: TItem) => {
    const resolvedValue = resolveValue(item);
    return resolvedValue ? multiValue.includes(resolvedValue) : false;
  }

  const handleMultiSelect = (item: TItem) => {
    const v = resolveValue(item);
    if (!v) return;
    multiOnChange?.(
      isItemSelected(item)
        ? multiValue.filter((x) => x !== v)
        : [...multiValue, v]
    );
  };

  /** "all" | "some" | "none" */
  const groupSelectionState = (group: GroupSearchDropdownGroup<TItem>) => {
    const groupValues = group.items.map(resolveValue);
    const selected = groupValues.filter((v) => v && multiValue.includes(v));
    if (selected.length === 0) return "none";
    if (selected.length === groupValues.length) return "all";
    return "some";
  };

  const handleGroupSelect = (group: GroupSearchDropdownGroup<TItem>) => {
    const state = groupSelectionState(group);
    const groupValues = group.items.map(resolveValue);
    if (state === "all") {
      multiOnChange?.(multiValue.filter((v) => !groupValues.includes(v)));
    } else {
      const merged = Array.from(new Set([...multiValue, ...groupValues.filter(v => v !== undefined)]));
      multiOnChange?.(merged);
    }
  };

  const triggerLabel = React.useMemo(() => {
    if (!multiple) return selectedSingle?.name || placeholder;
    if (multiValue.length === 0) return placeholder;
    if (multiValue.length === 1) {
      return allItems.find((i) => resolveValue(i) === multiValue[0])?.name ?? placeholder;
    }
    return `${multiValue.length} selected`;
  }, [multiple, selectedSingle, multiValue, allItems, placeholder, resolveValue]);

  const hasValue = multiple ? multiValue.length > 0 : !!singleValue;

  return (
    <div className={cn("relative w-full", className)}>
      <div className="flex flex-col gap-2">
        {/* Floating label */}
        <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
          {label}
        </label>

        <div className="relative">
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <div
                className={cn(
                  "w-full h-10 px-3 py-2 border cursor-pointer flex items-center justify-between",
                  errorMessage ? "border-red-500" : "border-gray-300",
                  inputClassname
                )}
                onClick={() => setOpen(true)}
              >
                <span className={cn(hasValue ? "text-black line-clamp-1" : "text-gray-400", placeholderClassname)}>
                  {triggerLabel}
                </span>
                <ChevronDownIcon className="h-4 w-4 opacity-50" />
              </div>
            </PopoverTrigger>

            <PopoverContent
              className="p-0 w-[--radix-popover-trigger-width] overflow-hidden"
              align="start"
              onWheel={(e) => e.stopPropagation()}
            >
              <Command className="w-full" shouldFilter={false}>
                <CommandInput
                  placeholder={searchPlaceholder}
                  className="h-9 border-none"
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList
                  className="max-h-[300px] overflow-hidden"
                  onWheel={(e) => e.stopPropagation()}
                >
                  <ScrollArea className="h-[300px] w-full" hideScrollToTop>
                    {filteredGroups.length === 0 && (
                      <CommandEmpty>{noResultsText}</CommandEmpty>
                    )}
                    {filteredGroups.map((group) => {
                      const gState = multiple
                        ? groupSelectionState(group)
                        : null;

                      return (
                        <CommandGroup
                          key={group.groupName}
                          heading={
                            <div className={cn(
                              "px-2 py-1.5 text-xs font-bold uppercase tracking-widest text-primary bg-primary/5 border-b border-primary/10 w-full sticky top-0 z-10", 
                              multiple && "cursor-pointer hover:bg-primary/10 flex items-center gap-1.5"
                            )}
                              onClick={
                                multiple
                                  ? () => handleGroupSelect(group)
                                  : undefined
                              }
                            >
                              {multiple && (
                                <span className="inline-flex items-center justify-center w-3.5 h-3.5 border border-primary rounded-sm shrink-0">
                                  {gState === "all" && (
                                    <Check className="w-2.5 h-2.5 text-primary" />
                                  )}
                                  {gState === "some" && (
                                    <Minus className="w-2.5 h-2.5 text-primary" />
                                  )}
                                </span>
                              )}
                              {group.groupName}
                            </div>
                          }
                        >
                          {group.items.map((item, idx) => {
                            const itemSelected = multiple
                              ? isItemSelected(item)
                              : resolveValue(item) == singleValue;

                            return (
                              <CommandItem
                                key={idx}
                                value={item.name}
                                onSelect={() =>
                                  multiple
                                    ? handleMultiSelect(item)
                                    : handleSingleSelect(item)
                                }
                                className="pl-6 cursor-pointer"
                              >
                                <Check
                                  className={cn(
                                    "mr-1 h-3 w-3",
                                    itemSelected ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <span className="text-sm">{item.name}</span>
                              </CommandItem>
                          )})}
                        </CommandGroup>
                        )
                    })}
                    <ScrollBar orientation="vertical" />
                  </ScrollArea>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Error message slot */}
      {errorMessage && (
        <div className="h-[20px] text-red-500 text-xs mt-[5px]">
          {errorMessage || ""}
        </div>
      )}
    </div>
  );
}
